import React from "react";
import { Link } from "react-router-dom";
import Navbar from "../../Components/common/Navbar";
import { useTranslation } from "react-i18next";
import {
  ArrowRight,
  Award,
  BookOpen,
  BriefcaseBusiness,
  CalendarDays,
  CheckCircle2,
  Clock3,
  FileCheck2,
  Globe2,
  GraduationCap,
  Laptop2,
  Lightbulb,
  Rocket,
  Search,
  Sparkles,
  Target,
  Users,
  WalletCards,
} from "lucide-react";

export default function InternshipPage() {
  const { t } = useTranslation();

  const programmeSteps = [
    {
      number: "01",
      title: "APPLY FOR THE INTERNSHIP",
      description:
        "Register your interest and join the WiLearn Virtual Internship Programme.",
      icon: FileCheck2,
    },
    {
      number: "02",
      title: "SELECT YOUR COURSES",
      description:
        "Choose relevant courses based on your area of interest, career goals, skills, and professional development requirements.",
      extra:
        "Participants can select courses from the available WiLearn learning categories.",
      icon: BookOpen,
    },
    {
      number: "03",
      title: "GET SPECIAL DISCOUNTED FEES",
      description:
        "Virtual Interns are eligible to access selected courses and learning opportunities at special discounted programme fees, subject to the applicable terms and offers.",
      icon: WalletCards,
    },
    {
      number: "04",
      title: "LEARN AND DEVELOP SKILLS",
      description:
        "Complete your selected courses and engage in structured learning activities designed to strengthen your knowledge and professional capabilities.",
      icon: GraduationCap,
    },
    {
      number: "05",
      title: "COMPLETE INTERNSHIP ACTIVITIES",
      description:
        "Participate in assigned internship tasks, projects, research, content activities, practical assignments, or other approved learning engagements.",
      icon: BriefcaseBusiness,
    },
    {
      number: "06",
      title: "BUILD YOUR EXPERIENCE",
      description:
        "Develop valuable skills in areas such as:",
      icon: Rocket,
    },
    {
      number: "07",
      title: "SUCCESSFUL COMPLETION",
      description:
        "Upon fulfilling the applicable programme requirements, participants may receive appropriate recognition, certificates, or other completion credentials as determined by WiLearn.",
      icon: Award,
    },
  ];

  const skills = [
    "Professional Communication",
    "Research and Analysis",
    "Digital Skills",
    "Project Coordination",
    "Content Development",
    "Leadership and Teamwork",
    "Innovation and Problem Solving",
    "Career and Professional Development",
  ];

  const benefits = [
    {
      icon: Globe2,
      title: "Learn from Anywhere",
      description:
        "Participate in the programme virtually without geographical limitations.",
    },
    {
      icon: BookOpen,
      title: "Choose Your Learning Path",
      description:
        "Select courses that match your interests and career aspirations.",
    },
    {
      icon: WalletCards,
      title: "Special Discounted Fees",
      description:
        "Access selected learning opportunities at discounted fees available for programme participants.",
    },
    {
      icon: Clock3,
      title: "Flexible Programme",
      description:
        "Complete your learning and internship activities within a 3-month structured programme period.",
    },
    {
      icon: BriefcaseBusiness,
      title: "Gain Practical Exposure",
      description:
        "Participate in meaningful assignments and activities that support professional development.",
    },
    {
      icon: Rocket,
      title: "Build Your Professional Profile",
      description:
        "Strengthen your knowledge, skills, experience, and confidence for future opportunities.",
    },
  ];

  const applicants = [
    "Students",
    "Graduates",
    "Young Professionals",
    "Researchers",
    "Educators",
    "Career Aspirants",
    "Lifelong Learners",
    "Individuals seeking professional skill development",
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar page="internship" />

      {/* ================= HERO ================= */}
      <section className="relative overflow-hidden bg-gradient-to-br from-violet-950 via-violet-900 to-purple-800">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute -left-24 top-10 h-72 w-72 rounded-full bg-fuchsia-400 blur-3xl" />
          <div className="absolute right-0 top-0 h-96 w-96 rounded-full bg-violet-400 blur-3xl" />
          <div className="absolute bottom-0 left-1/2 h-72 w-72 rounded-full bg-pink-400 blur-3xl" />
        </div>

        <div className="relative mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8 lg:py-20">
          <div className="mx-auto max-w-6xl text-center">
            <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-4 py-2 text-sm font-semibold text-violet-100 backdrop-blur-md">
              <GraduationCap className="h-4 w-4" />
              {t("WiLearn Virtual Internship Programme")}
            </div>

            <h1 className="text-4xl font-black leading-tight text-white sm:text-5xl lg:text-6xl">
              {t("WiLearn Virtual Internship Programme")}
            </h1>

            <p className="mt-4 text-lg font-bold uppercase tracking-[0.2em] text-pink-200 sm:text-xl">
              {t("LEARN. INTERN. ACHIEVE.")}
            </p>

            <p className="mx-auto mt-6 max-w-5xl text-justify text-lg leading-8 text-violet-100 sm:text-xl">
              {t(
                "WiLearn offers a unique Virtual Internship Programme designed to help learners gain knowledge, practical exposure, professional skills, and valuable learning experience from anywhere in the world."
              )}
            </p>

            <p className="mx-auto mt-4 max-w-5xl text-justify text-base leading-8 text-violet-100 sm:text-lg">
              {t(
                "Our flexible internship model allows participants to combine online learning with structured internship activities, making professional development more accessible and convenient."
              )}
            </p>

            <div className="mt-8 flex flex-wrap justify-center gap-4">
              <a
                href="#apply"
                className="inline-flex items-center gap-2 rounded-full bg-white px-7 py-3 font-bold text-violet-800 shadow-lg transition duration-300 hover:-translate-y-1 hover:bg-violet-50"
              >
                {t("Join as a Virtual Intern")}
                <ArrowRight className="h-4 w-4" />
              </a>

              <a
                href="#programme"
                className="rounded-full border border-white/30 bg-white/10 px-7 py-3 font-semibold text-white backdrop-blur-md transition hover:bg-white/20"
              >
                {t("View Programme")}
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ================= JOIN AS VIRTUAL INTERN ================= */}
      <section
        id="programme"
        className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8"
      >
        <div className="grid gap-10 lg:grid-cols-[1fr_1fr] lg:items-center">
          <div>
            <p className="text-sm font-bold uppercase tracking-[0.25em] text-violet-600">
              {t("JOIN AS A VIRTUAL INTERN")}
            </p>

            <h2 className="mt-3 text-3xl font-bold leading-tight text-slate-900 sm:text-4xl">
              {t("Begin your journey towards professional growth")}
            </h2>

            <p className="mt-6 text-justify text-lg leading-8 text-slate-600">
              {t(
                "Become a part of the WiLearn Virtual Internship Programme and begin your journey towards professional growth."
              )}
            </p>

            <div className="mt-8 grid gap-4 sm:grid-cols-3">
              <div className="rounded-2xl border border-violet-100 bg-white p-5 shadow-sm">
                <CalendarDays className="h-7 w-7 text-violet-600" />
                <p className="mt-4 text-sm font-semibold text-slate-500">
                  {t("Programme Duration")}
                </p>
                <p className="mt-1 text-xl font-bold text-slate-900">
                  {t("3 Months")}
                </p>
              </div>

              <div className="rounded-2xl border border-violet-100 bg-white p-5 shadow-sm">
                <Laptop2 className="h-7 w-7 text-violet-600" />
                <p className="mt-4 text-sm font-semibold text-slate-500">
                  {t("Mode of Internship")}
                </p>
                <p className="mt-1 text-xl font-bold text-slate-900">
                  {t("100% Virtual / Online")}
                </p>
              </div>

              <div className="rounded-2xl border border-violet-100 bg-white p-5 shadow-sm">
                <Clock3 className="h-7 w-7 text-violet-600" />
                <p className="mt-4 text-sm font-semibold text-slate-500">
                  {t("Flexible Learning")}
                </p>
                <p className="mt-1 text-base font-bold leading-6 text-slate-900">
                  {t(
                    "Learn and complete internship activities at your convenience within the programme period."
                  )}
                </p>
              </div>
            </div>
          </div>

          <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-violet-600 via-purple-600 to-fuchsia-600 p-8 text-white shadow-2xl sm:p-10">
            <div className="absolute -right-12 -top-12 h-40 w-40 rounded-full bg-white/10" />
            <div className="absolute -bottom-16 -left-16 h-48 w-48 rounded-full bg-white/10" />

            <div className="relative">
              <Sparkles className="h-12 w-12" />

              <h3 className="mt-6 text-3xl font-bold">
                {t("Flexible. Practical. Accessible.")}
              </h3>

              <p className="mt-5 text-justify text-lg leading-8 text-violet-100">
                {t(
                  "Combine structured online learning with practical internship activities while developing knowledge and professional capabilities."
                )}
              </p>

              <div className="mt-8 grid grid-cols-2 gap-3">
                {[
                  "Online Learning",
                  "Skill Development",
                  "Practical Exposure",
                  "Professional Growth",
                ].map((item) => (
                  <div
                    key={item}
                    className="flex items-center gap-2 rounded-xl bg-white/10 px-3 py-3 text-sm font-semibold backdrop-blur"
                  >
                    <CheckCircle2 className="h-4 w-4 shrink-0 text-pink-200" />
                    {t(item)}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ================= HOW IT WORKS ================= */}
      <section className="bg-white py-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-4xl text-center">
            <p className="text-sm font-bold uppercase tracking-[0.25em] text-violet-600">
              {t("HOW THE PROGRAMME WORKS")}
            </p>

            <h2 className="mt-3 text-3xl font-bold text-slate-900 sm:text-4xl">
              {t("A structured path from learning to experience")}
            </h2>
          </div>

          <div className="mt-12 space-y-5">
            {programmeSteps.map((step, index) => {
              const Icon = step.icon;

              return (
                <div
                  key={step.number}
                  className="group grid gap-5 rounded-3xl border border-slate-200 bg-slate-50 p-6 transition duration-300 hover:-translate-y-1 hover:border-violet-200 hover:bg-white hover:shadow-xl md:grid-cols-[90px_1fr]"
                >
                  <div className="flex md:block">
                    <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-violet-100 text-violet-700 transition group-hover:bg-violet-600 group-hover:text-white">
                      <Icon className="h-7 w-7" />
                    </div>
                  </div>

                  <div>
                    <div className="flex flex-wrap items-center gap-3">
                      <span className="text-sm font-black tracking-widest text-violet-500">
                        {step.number}
                      </span>

                      <h3 className="text-xl font-bold text-slate-900">
                        {t(step.title)}
                      </h3>
                    </div>

                    <p className="mt-3 text-justify text-base leading-7 text-slate-600">
                      {t(step.description)}
                    </p>

                    {step.extra && (
                      <p className="mt-2 text-justify text-base leading-7 text-slate-600">
                        {t(step.extra)}
                      </p>
                    )}

                    {index === 5 && (
                      <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                        {skills.map((skill) => (
                          <div
                            key={skill}
                            className="flex items-start gap-2 rounded-xl border border-violet-100 bg-white p-3"
                          >
                            <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-violet-600" />
                            <span className="text-sm font-semibold leading-5 text-slate-700 text-left">
                              {t(skill)}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ================= WHY JOIN ================= */}
      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-4xl text-center">
          <p className="text-sm font-bold uppercase tracking-[0.25em] text-violet-600">
            {t("WHY JOIN THE WILEARN VIRTUAL INTERNSHIP?")}
          </p>

          <h2 className="mt-3 text-3xl font-bold text-slate-900 sm:text-4xl">
            {t("Build skills, experience and professional confidence")}
          </h2>
        </div>

        <div className="mt-12 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
          {benefits.map((benefit) => {
            const Icon = benefit.icon;

            return (
              <div
                key={benefit.title}
                className="group rounded-3xl border border-slate-200 bg-white p-7 shadow-sm transition duration-300 hover:-translate-y-1 hover:border-violet-200 hover:shadow-xl"
              >
                <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-violet-100 to-pink-100 text-violet-700 transition group-hover:scale-105">
                  <Icon className="h-7 w-7" />
                </div>

                <h3 className="mt-5 text-xl font-bold text-slate-900">
                  {t(benefit.title)}
                </h3>

                <p className="mt-3 text-justify leading-7 text-slate-600">
                  {t(benefit.description)}
                </p>
              </div>
            );
          })}
        </div>
      </section>

      {/* ================= PROGRAMME STRUCTURE ================= */}
      <section className="bg-slate-900 py-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-4xl text-center">
            <p className="text-sm font-bold uppercase tracking-[0.25em] text-violet-300">
              {t("PROGRAMME STRUCTURE")}
            </p>

            <h2 className="mt-3 text-3xl font-bold text-white sm:text-4xl">
              {t("Your 3-month virtual internship journey")}
            </h2>
          </div>

          <div className="mt-12 overflow-hidden rounded-3xl border border-white/10 bg-white/5 backdrop-blur">
            <div className="hidden grid-cols-[220px_1fr] bg-violet-600 px-7 py-5 text-white md:grid">
              <div className="font-bold">{t("Phase")}</div>
              <div className="font-bold">{t("Activity")}</div>
            </div>

            {[
              {
                month: "Month 1",
                activity: "Orientation, Course Selection and Learning",
              },
              {
                month: "Month 2",
                activity: "Skill Development and Internship Activities",
              },
              {
                month: "Month 3",
                activity:
                  "Project Completion, Assessment and Final Submission",
              },
            ].map((item, index) => (
              <div
                key={item.month}
                className={`grid gap-3 px-7 py-6 text-slate-200 md:grid-cols-[220px_1fr] ${
                  index !== 2 ? "border-b border-white/10" : ""
                }`}
              >
                <div>
                  <span className="mb-1 block text-xs font-bold uppercase tracking-wider text-violet-300 md:hidden">
                    {t("Phase")}
                  </span>
                  <span className="font-bold text-white">{t(item.month)}</span>
                </div>

                <div>
                  <span className="mb-1 block text-xs font-bold uppercase tracking-wider text-violet-300 md:hidden">
                    {t("Activity")}
                  </span>
                  <span className="leading-7">{t(item.activity)}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ================= WHO CAN APPLY ================= */}
      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
        <div className="rounded-3xl border border-violet-100 bg-gradient-to-br from-white via-violet-50 to-pink-50 p-7 sm:p-10 lg:p-12">
          <div className="grid gap-10 lg:grid-cols-[0.9fr_1.1fr] lg:items-center">
            <div>
              <p className="text-sm font-bold uppercase tracking-[0.25em] text-violet-600">
                {t("WHO CAN APPLY?")}
              </p>

              <h2 className="mt-3 text-3xl font-bold leading-tight text-slate-900 sm:text-4xl">
                {t("Open to learners at different stages of growth")}
              </h2>

              <p className="mt-5 text-justify text-lg leading-8 text-slate-600">
                {t(
                  "The WiLearn Virtual Internship Programme may be suitable for:"
                )}
              </p>
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              {applicants.map((applicant) => (
                <div
                  key={applicant}
                  className="flex items-center gap-3 rounded-2xl border border-white bg-white p-4 shadow-sm"
                >
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-violet-100">
                    <CheckCircle2 className="h-5 w-5 text-violet-600" />
                  </div>

                  <span className="font-semibold leading-6 text-slate-800 text-left">
                    {t(applicant)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ================= APPLY ================= */}
      <section id="apply" className="bg-white py-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="relative overflow-hidden rounded-[2rem] bg-gradient-to-br from-violet-700 via-purple-700 to-fuchsia-600 px-6 py-12 shadow-2xl sm:px-10 lg:px-14 lg:py-14">
            <div className="absolute -right-20 top-0 h-72 w-72 rounded-full bg-white/10 blur-2xl" />
            <div className="absolute -bottom-24 -left-20 h-72 w-72 rounded-full bg-pink-400/20 blur-2xl" />

            <div className="relative grid gap-10 lg:grid-cols-[1fr_0.8fr] lg:items-center">
              <div>
                <p className="text-sm font-black uppercase tracking-[0.3em] text-pink-200">
                  {t("APPLY")}
                </p>

                <h2 className="mt-4 text-3xl font-black text-white sm:text-4xl">
                  {t("JOIN. LEARN. INTERN. GROW.")}
                </h2>

                <h3 className="mt-3 text-2xl font-bold text-white">
                  {t("JOIN AS A VIRTUAL INTERN TODAY")}
                </h3>

                <div className="mt-8 space-y-4">
                  {[
                    ["Programme Duration:", "3 Months"],
                    ["Mode:", "Virtual / Online"],
                    ["Learning:", "Select Your Preferred Courses"],
                    ["Benefit:", "Special Discounted Programme Fees"],
                  ].map(([label, value]) => (
                    <div
                      key={label}
                      className="flex items-start gap-3 text-violet-100"
                    >
                      <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-pink-200" />

                      <p className="leading-7">
                        <span className="font-bold text-white">{t(label)}</span>{" "}
                        {t(value)}
                      </p>
                    </div>
                  ))}
                </div>

                <p className="mt-8 text-lg font-bold uppercase leading-8 tracking-wide text-white">
                  {t(
                    "START YOUR PROFESSIONAL LEARNING JOURNEY WITH WILEARN"
                  )}
                </p>
              </div>

              <div className="rounded-3xl border border-white/20 bg-white/10 p-7 text-center backdrop-blur-md sm:p-9">
                <Rocket className="mx-auto h-14 w-14 text-pink-200" />

                <h3 className="mt-5 text-2xl font-bold text-white">
                  {t("Ready to get started?")}
                </h3>

                <p className="mt-3 text-justify leading-7 text-violet-100">
                  {t(
                    "Start your professional learning journey with WiLearn and become part of a flexible virtual internship experience."
                  )}
                </p>

                <Link
                  to="/register"
                  className="mt-7 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-white px-6 py-4 font-bold text-violet-700 shadow-lg transition duration-300 hover:-translate-y-1 hover:bg-violet-50"
                >
                  {t("Join as a Virtual Intern")}
                  <ArrowRight className="h-5 w-5" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}